import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Line, Doughnut, Bar,
} from 'react-chartjs-2';
import {
    Bell, CreditCard, UserPlus, ClipboardList, Receipt, Gift, Users,
    Cake, Briefcase, Heart, PartyPopper, Sparkles, ArrowRight,
} from 'lucide-react';
import { inr } from '../../utils/format';
import { fmtShortDate, fmtActivityTime } from '../../utils/dashboardMetrics';

export const CLIENT_STATUS_COLOR = {
    submitted: '#FFCA05',
    approved: '#00B09B',
    rejected: '#C3161C',
    draft: '#9ca3af',
};

export const TEAL_SCALE = ['#0a3530', '#0d5249', '#117a6d', '#1a9a88', '#3dbfb0', '#7dd9cc'];

/** Soft 1px shadow edge + light lift — reads as a border without a hard ring */
const SOFT_CARD_EDGE = 'shadow-[0_0_0_1px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.05),0_2px_8px_rgba(15,23,42,0.04)]';
const SOFT_CARD_EDGE_ROSE = 'shadow-[0_0_0_1px_rgba(225,29,72,0.14),0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(225,29,72,0.06)]';

const PILL_GRADIENTS = [
    'from-[#0a3530] to-[#0d5249]',
    'from-[#0d5249] to-[#117a6d]',
    'from-[#117a6d] to-[#1a9a88]',
    'from-[#1a9a88] to-[#3dbfb0]',
    'from-[#3dbfb0] to-[#7dd9cc]',
];

function DashCard({ children, className = '' }) {
    return (
        <div className={`rounded-2xl bg-gradient-to-br from-ink-50/90 via-white to-brand-teal-50/25 p-4 ${SOFT_CARD_EDGE} ${className}`}>
            {children}
        </div>
    );
}

function buildDonutOptions({ formatValue }) {
    return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '74%',
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const sum = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        const pct = sum > 0 ? Math.round((ctx.parsed / sum) * 100) : 0;
                        const val = formatValue ? formatValue(ctx.parsed) : ctx.parsed;
                        return ` ${ctx.label}: ${val} (${pct}%)`;
                    },
                },
            },
        },
    };
}

/* ─── Filters (reference top bar) ─── */
export function DashboardFilters({ period, onPeriodChange, status, onStatusChange, statusOptions }) {
    const selectCls = 'rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink-800 focus:outline-none focus:ring-2 focus:ring-brand-teal/30';
    return (
        <div className="flex flex-wrap items-center gap-2">
            <select className={selectCls} value={period} onChange={e => onPeriodChange(e.target.value)} aria-label="Period">
                <option value="6m">Last 6 months</option>
                <option value="12m">Last 12 months</option>
                <option value="all">All time</option>
            </select>
            <select className={selectCls} value={status} onChange={e => onStatusChange(e.target.value)} aria-label="Status">
                {(statusOptions || [
                    { value: 'all', label: 'All statuses' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'submitted', label: 'Pending review' },
                    { value: 'rejected', label: 'Rejected' },
                    { value: 'draft', label: 'Draft' },
                ]).map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </div>
    );
}

/* ─── Financial summary header (replaces gradient pills) ─── */
function FinTile({ label, value }) {
    return (
        <div className={`rounded-lg bg-white px-3 py-2.5 ${SOFT_CARD_EDGE}`}>
            <p className="text-[9px] uppercase tracking-[0.14em] text-ink-500 leading-tight">{label}</p>
            <p className="mt-1 text-sm font-medium tabular-nums text-ink-800 truncate">{value}</p>
        </div>
    );
}

export function FinancialSummarySection({ financials, title = 'Financial summary', subtitle, overdueLink }) {
    const rate = financials.collectionRate ?? 0;
    const tiles = [
        { label: 'Total deal value', value: inr(financials.totalDeal) },
        { label: 'Total payable', value: inr(financials.totalPayable) },
        { label: 'Total collected', value: inr(financials.totalCollected) },
        { label: 'Balance due', value: inr(financials.totalBalance) },
        { label: 'Total GST', value: inr(financials.totalGst) },
        { label: 'Total TDS', value: inr(financials.totalTds) },
        { label: 'Token received', value: inr(financials.totalToken) },
        { label: 'Instalments', value: inr(financials.totalInstalments) },
        { label: 'Total refunds', value: inr(financials.totalRefunded) },
    ];

    return (
        <div className={`rounded-2xl bg-gradient-to-br from-ink-50 via-white to-brand-teal-50/30 p-4 sm:p-5 ${SOFT_CARD_EDGE}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                    <p className="text-sm font-medium text-ink-900">{title}</p>
                    {subtitle && <p className="text-xs text-ink-500 mt-0.5">{subtitle}</p>}
                </div>
                <span className="text-[10px] text-ink-400 tabular-nums">
                    {financials.activeClients ?? 0} active client{(financials.activeClients ?? 0) === 1 ? '' : 's'}
                </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                {tiles.map(t => (
                    <FinTile key={t.label} label={t.label} value={t.value} />
                ))}
            </div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className={`rounded-xl bg-white px-4 py-3 ${SOFT_CARD_EDGE} flex flex-col sm:flex-row sm:items-center gap-3`}>
                    <div className="shrink-0">
                        <p className="text-[9px] uppercase tracking-[0.14em] text-brand-teal-700">Collection rate</p>
                        <p className="text-2xl font-medium tabular-nums text-ink-900 mt-0.5">{rate}%</p>
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                        <p className="text-xs text-ink-600">
                            <span className="text-brand-teal-700">{inr(financials.totalCollected)}</span>
                            {' '}collected of{' '}
                            <span className="text-ink-800">{inr(financials.totalPayable)}</span>
                        </p>
                        <div className="h-1.5 rounded-full bg-ink-100 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-brand-teal to-brand-teal-700 transition-all"
                                style={{ width: `${Math.min(100, rate)}%` }}
                            />
                        </div>
                    </div>
                    <div className="relative h-14 w-14 shrink-0 hidden sm:block">
                        <svg className="h-full w-full -rotate-90" viewBox="0 0 36 36" aria-hidden>
                            <circle cx="18" cy="18" r="15" fill="none" stroke="#e8edf0" strokeWidth="3" />
                            <circle
                                cx="18" cy="18" r="15" fill="none" stroke="#0d5249" strokeWidth="3"
                                strokeLinecap="round"
                                strokeDasharray={`${(rate / 100) * 94.2} 94.2`}
                            />
                        </svg>
                        <span className="absolute inset-0 grid place-items-center text-[10px] font-medium text-ink-700 tabular-nums">
                            {Math.round(rate)}%
                        </span>
                    </div>
                </div>

                <div className={`rounded-xl px-4 py-3 flex flex-col justify-between ${
                    financials.overdueClients > 0
                        ? `bg-rose-50/60 ${SOFT_CARD_EDGE_ROSE}`
                        : `bg-white ${SOFT_CARD_EDGE}`
                }`}>
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="text-[9px] uppercase tracking-[0.14em] text-rose-700/90">
                                Overdue {financials.agingDays ?? 45}+ days
                            </p>
                            <p className="text-xl font-medium tabular-nums text-rose-800 mt-1">
                                {financials.overdueClients} client{financials.overdueClients === 1 ? '' : 's'}
                            </p>
                            <p className="text-sm font-medium tabular-nums text-rose-700/90 mt-0.5">
                                {inr(financials.overdueBalance)}
                            </p>
                        </div>
                        {financials.overdueClients > 0 && (
                            <span className="text-lg" aria-hidden>⚠️</span>
                        )}
                    </div>
                    {overdueLink && financials.overdueClients > 0 && (
                        <Link to={overdueLink} className="text-xs font-medium text-rose-800 hover:underline mt-2 inline-flex items-center gap-1">
                            View details <ArrowRight className="h-3 w-3" />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}

/** @deprecated use FinancialSummarySection */
export function MetricPills({ items }) {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {items.map((item, i) => (
                <div
                    key={item.label}
                    className={`rounded-xl bg-gradient-to-br ${PILL_GRADIENTS[i % PILL_GRADIENTS.length]} px-4 py-3.5 text-white shadow-sm min-h-[72px] flex flex-col justify-center`}
                >
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/75 truncate">{item.label}</p>
                    <p className="text-lg sm:text-xl font-black tabular-nums leading-tight truncate mt-0.5">{item.value}</p>
                </div>
            ))}
        </div>
    );
}

function DonutSlot({ title, segments, icon: Icon, formatValue }) {
    const active = segments.filter(s => s.value > 0);
    const total = active.reduce((s, x) => s + x.value, 0);

    return (
        <div className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
            <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.12em] text-ink-500 mb-2">{title}</p>
                {active.length === 0 ? (
                    <p className="text-xs text-ink-400">No data yet</p>
                ) : (
                    <ul className="space-y-1.5">
                        {active.map(s => {
                            const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
                            const val = formatValue ? formatValue(s.value) : s.value;
                            return (
                                <li key={s.label} className="flex items-start gap-2 text-xs text-ink-600">
                                    <span className="h-2 w-2 rounded-full shrink-0 mt-1 opacity-80" style={{ backgroundColor: s.color }} />
                                    <span className="flex-1 leading-snug">{s.label}</span>
                                    <span className="font-medium tabular-nums shrink-0 text-ink-800">
                                        {formatValue ? `${val} · ${pct}%` : `${val} (${pct}%)`}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            <div className="relative h-[96px] w-[96px] shrink-0">
                {active.length === 0 ? (
                    <div className="h-full w-full rounded-full border-2 border-dashed border-ink-200 bg-ink-50 grid place-items-center">
                        {Icon && <Icon className="h-5 w-5 text-ink-300" strokeWidth={2} />}
                    </div>
                ) : (
                    <>
                        <Doughnut
                            data={{
                                labels: active.map(s => s.label),
                                datasets: [{
                                    data: active.map(s => s.value),
                                    backgroundColor: active.map(s => s.color),
                                    borderWidth: 2,
                                    borderColor: '#fff',
                                }],
                            }}
                            options={buildDonutOptions({ formatValue })}
                        />
                        <div className="absolute inset-0 grid place-items-center pointer-events-none">
                            {Icon ? (
                                <Icon className="h-4 w-4 text-brand-teal-600" strokeWidth={2} />
                            ) : (
                                <span className="text-sm font-medium text-ink-700 tabular-nums">{total}</span>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/* ─── Portfolio breakdown (legend left, donut right — reference style) ─── */
export function BreakdownCard({ title = 'Breakdown', sections }) {
    return (
        <DashCard className="h-full flex flex-col">
            <h3 className="text-sm font-medium text-ink-800 mb-2 shrink-0">{title}</h3>
            <div className="flex-1 flex flex-col justify-center divide-y divide-ink-100/80">
                {sections.map(s => (
                    <DonutSlot
                        key={s.title}
                        title={s.title}
                        segments={s.segments}
                        icon={s.icon}
                        formatValue={s.formatValue}
                    />
                ))}
            </div>
        </DashCard>
    );
}

/** @deprecated use BreakdownCard */
export function DonutTripleRow({ charts }) {
    return <BreakdownCard sections={charts} />;
}

/* ─── Line trend chart (reference middle top) ─── */
export function TrendLineChart({ title, trend }) {
    const empty = trend.every(t => t.newClients === 0 && t.collected === 0);
    const chartData = {
        labels: trend.map(t => t.label),
        datasets: [
            {
                label: 'New clients',
                data: trend.map(t => t.newClients),
                borderColor: '#117a6d',
                backgroundColor: 'rgba(17,122,109,0.06)',
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointBackgroundColor: '#117a6d',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
            },
            {
                label: 'Collected (₹ Lakhs)',
                data: trend.map(t => Math.round(t.collected / 100000) / 10),
                borderColor: '#3dbfb0',
                backgroundColor: 'transparent',
                borderDash: [5, 4],
                tension: 0.4,
                pointRadius: 3,
                yAxisID: 'y1',
            },
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 8, padding: 8, font: { size: 10 } } },
        },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0 } },
            y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } }, grid: { color: '#f1f5f9' } },
            y1: {
                position: 'right',
                beginAtZero: true,
                grid: { display: false },
                ticks: { font: { size: 9 } },
            },
        },
    };

    return (
        <DashCard className="h-full flex flex-col">
            <h3 className="text-sm font-medium text-ink-800 mb-2 shrink-0">{title}</h3>
            <div className="flex-1 min-h-[200px]">
                {empty ? (
                    <div className="h-full grid place-items-center text-sm text-ink-500">No trend data yet</div>
                ) : (
                    <Line data={chartData} options={options} />
                )}
            </div>
        </DashCard>
    );
}

/* ─── Horizontal bar chart (reference middle bottom) ─── */
export function HorizontalBarChart({ title, items, formatValue }) {
    const sorted = [...items].sort((a, b) => b.value - a.value).slice(0, 6);
    const empty = sorted.length === 0 || sorted.every(i => i.value === 0);
    const colors = sorted.map((_, i) => TEAL_SCALE[Math.min(i, TEAL_SCALE.length - 1)]);

    const chartData = {
        labels: sorted.map(i => i.label),
        datasets: [{
            data: sorted.map(i => i.value),
            backgroundColor: colors,
            borderRadius: 6,
            barThickness: 14,
        }],
    };
    const options = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` ${formatValue ? formatValue(ctx.parsed.x) : ctx.parsed.x}`,
                },
            },
        },
        scales: {
            x: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { font: { size: 9 } } },
            y: { grid: { display: false }, ticks: { font: { size: 10, weight: '500' } } },
        },
    };

    return (
        <DashCard className="h-full flex flex-col">
            <h3 className="text-xs font-bold text-ink-900 mb-1">{title}</h3>
            <div className="h-[120px] flex-1">
                {empty ? (
                    <div className="h-full grid place-items-center text-sm text-ink-500">No data yet</div>
                ) : (
                    <Bar data={chartData} options={options} />
                )}
            </div>
        </DashCard>
    );
}

/* ─── Semi-circular gauge (reference right top) ─── */
export function SemiGaugeCard({ title, value, max = 100, displayValue, subtitle, compact = false }) {
    const pct = Math.min(1, Math.max(0, value / max));
    const r = compact ? 56 : 72;
    const cx = 100;
    const cy = compact ? 82 : 88;
    const strokeW = compact ? 10 : 14;
    const pad = strokeW / 2 + 2;
    const startAngle = 180;
    const endAngle = 0;
    const filledAngle = startAngle - (startAngle - endAngle) * pct;

    const polar = (angleDeg) => {
        const rad = (angleDeg * Math.PI) / 180;
        return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
    };
    const arcPath = (a1, a2) => {
        const s = polar(a1);
        const e = polar(a2);
        const large = Math.abs(a2 - a1) > 180 ? 1 : 0;
        return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
    };

    const vbH = cy + pad;

    return (
        <div className={compact ? 'overflow-visible' : 'h-full flex flex-col items-center overflow-visible'}>
            {!compact && (
                <>
                    <h3 className="text-xs font-bold text-ink-900 self-start w-full">{title}</h3>
                    {subtitle && <p className="text-[10px] text-ink-500 self-start w-full">{subtitle}</p>}
                </>
            )}
            <div className={`relative w-full overflow-visible ${compact ? 'max-w-[200px] mx-auto pt-1 pb-2' : 'max-w-[220px]'}`}>
                <svg viewBox={`0 0 200 ${vbH}`} className="w-full overflow-visible" aria-hidden preserveAspectRatio="xMidYMid meet">
                    <path d={arcPath(startAngle, endAngle)} fill="none" stroke="#e8edf0" strokeWidth={strokeW} strokeLinecap="round" />
                    {pct > 0 && (
                        <path d={arcPath(startAngle, filledAngle)} fill="none" stroke="#117a6d" strokeWidth={strokeW} strokeLinecap="round" />
                    )}
                </svg>
                <div className="absolute inset-x-0 bottom-1 text-center pointer-events-none">
                    <span className="text-xl font-medium text-ink-800 tabular-nums leading-none">
                        {displayValue ?? value}
                    </span>
                </div>
            </div>
        </div>
    );
}

/* ─── Gauge + lists combined (saves vertical space) ─── */
export function SidePanel({ gauge, lists }) {
    return (
        <DashCard className="h-full flex flex-col gap-3 overflow-visible">
            <div className="shrink-0 overflow-visible pb-1">
                <h3 className="text-sm font-bold text-ink-900">{gauge.title}</h3>
                {gauge.subtitle && <p className="text-xs text-ink-500 mt-0.5 mb-1">{gauge.subtitle}</p>}
                <SemiGaugeCard {...gauge} compact />
            </div>
            <div className="flex-1 flex flex-col gap-3">
                {lists.map(list => (
                    <div key={list.title} className="border-t border-ink-100 pt-3 flex-1">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-ink-500">{list.title}</h4>
                            {list.linkTo && (
                                <Link to={list.linkTo} className="text-[11px] font-semibold text-brand-teal-700 hover:underline">
                                    {list.linkLabel || 'View →'}
                                </Link>
                            )}
                        </div>
                        {list.items.length === 0 ? (
                            <p className="text-xs text-ink-400">Nothing here</p>
                        ) : (
                            <ul className="space-y-1.5">
                                {list.items.slice(0, list.maxItems ?? 4).map(item => (
                                    <li key={item.label}>
                                        {item.to ? (
                                            <Link to={item.to} className="flex items-center gap-2 group">
                                                <span className="w-1 h-6 rounded-full shrink-0" style={{ backgroundColor: item.color || '#0d5249' }} />
                                                <span className="flex-1 text-xs text-ink-700 truncate group-hover:text-brand-teal-700">{item.label}</span>
                                                <span className="text-xs font-bold tabular-nums shrink-0">
                                                    {item.count != null ? item.count : (list.formatValue ? list.formatValue(item.value) : item.value)}
                                                </span>
                                            </Link>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <span className="w-1 h-6 rounded-full shrink-0" style={{ backgroundColor: item.color || '#0d5249' }} />
                                                <span className="flex-1 text-xs text-ink-700 truncate">{item.label}</span>
                                                <span className="text-xs font-bold tabular-nums shrink-0">
                                                    {item.count != null ? item.count : (list.formatValue ? list.formatValue(item.value) : item.value)}
                                                </span>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ))}
            </div>
        </DashCard>
    );
}

/* ─── Color-bar list (reference right bottom) ─── */
export function ColorBarList({ title, items, formatValue, linkTo, linkLabel }) {
    return (
        <DashCard className="h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-ink-900">{title}</h3>
                {linkTo && (
                    <Link to={linkTo} className="text-[11px] font-semibold text-brand-teal-700 hover:underline">
                        {linkLabel || 'View all →'}
                    </Link>
                )}
            </div>
            {items.length === 0 ? (
                <p className="text-sm text-ink-500 py-6 text-center flex-1">Nothing here yet</p>
            ) : (
                <ul className="space-y-3 flex-1">
                    {items.map(item => (
                        <li key={item.label}>
                            {item.to ? (
                                <Link to={item.to} className="flex items-center gap-3 group">
                                    <span className="w-1 h-9 rounded-full shrink-0" style={{ backgroundColor: item.color || '#0d5249' }} />
                                    <span className="flex-1 text-sm text-ink-700 group-hover:text-brand-teal-700 truncate">{item.label}</span>
                                    <span className="text-sm font-bold tabular-nums text-ink-900 shrink-0">
                                        {item.count != null ? item.count : (formatValue ? formatValue(item.value) : item.value)}
                                    </span>
                                </Link>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <span className="w-1 h-9 rounded-full shrink-0" style={{ backgroundColor: item.color || '#0d5249' }} />
                                    <span className="flex-1 text-sm text-ink-700 truncate">{item.label}</span>
                                    <span className="text-sm font-bold tabular-nums text-ink-900 shrink-0">
                                        {item.count != null ? item.count : (formatValue ? formatValue(item.value) : item.value)}
                                    </span>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </DashCard>
    );
}

const ACTIVITY_META = {
    payment: { icon: CreditCard, bg: 'bg-amber-50/50', ring: 'ring-amber-100/80' },
    review: { icon: ClipboardList, bg: 'bg-amber-50/50', ring: 'ring-amber-100/80' },
    client: { icon: Users, bg: 'bg-brand-teal-50/40', ring: 'ring-brand-teal/10' },
    expense: { icon: Receipt, bg: 'bg-brand-teal-50/40', ring: 'ring-brand-teal/10' },
    signup: { icon: UserPlus, bg: 'bg-brand-teal-50/40', ring: 'ring-brand-teal/10' },
};

/* ─── Recent activity feed ─── */
export function RecentActivityCard({ items, linkTo, linkLabel = 'View all' }) {
    const urgentCount = items.filter(i => i.urgent).length;

    return (
        <DashCard className="h-full flex flex-col overflow-hidden p-0">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-ink-100/80 bg-gradient-to-r from-brand-teal-50/25 to-white/90 shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="relative grid h-8 w-8 place-items-center rounded-xl bg-brand-teal-50 text-brand-teal-700 shrink-0 ring-1 ring-brand-teal/10">
                        <Bell className="h-4 w-4" strokeWidth={2} />
                        {urgentCount > 0 && (
                            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-rose-400 text-[9px] font-medium text-white grid place-items-center">
                                {urgentCount}
                            </span>
                        )}
                    </span>
                    <div className="min-w-0">
                        <h3 className="text-sm font-medium text-ink-800">Recent activity</h3>
                        <p className="text-[10px] text-ink-400">{items.length} update{items.length === 1 ? '' : 's'}</p>
                    </div>
                </div>
                {linkTo && (
                    <Link to={linkTo} className="text-[11px] font-medium text-brand-teal-600 hover:text-brand-teal-700 hover:underline shrink-0">
                        {linkLabel} →
                    </Link>
                )}
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0">
                {items.length === 0 ? (
                    <p className="text-sm text-ink-400 text-center py-10">No recent activity</p>
                ) : (
                    items.map(item => {
                        const meta = ACTIVITY_META[item.type] || ACTIVITY_META.client;
                        const Icon = meta.icon;
                        return (
                            <Link
                                key={item.id}
                                to={item.href}
                                className={`block rounded-xl border border-ink-100/60 p-3 transition hover:shadow-sm hover:border-brand-teal/20 ${item.urgent ? meta.bg : 'bg-white/80'} ring-1 ${item.urgent ? meta.ring : 'ring-ink-100/50'}`}
                            >
                                <div className="flex gap-3">
                                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${item.urgent ? 'bg-white/80' : 'bg-ink-50/80'}`}>
                                        <Icon className="h-4 w-4 text-brand-teal-600" strokeWidth={2} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <p className="text-xs font-medium text-ink-800 leading-snug">{item.title}</p>
                                            <span className="text-[10px] font-medium text-ink-400 tabular-nums shrink-0">
                                                {fmtActivityTime(item.at)}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-ink-500 mt-1 line-clamp-2 leading-relaxed">{item.detail}</p>
                                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-brand-teal-600 mt-2">
                                            Open <ArrowRight className="h-3 w-3" />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        );
                    })
                )}
            </div>

            {linkTo && items.length > 0 && (
                <div className="px-4 py-2 border-t border-ink-100/80 bg-ink-50/40 shrink-0">
                    <Link to={linkTo} className="text-[11px] font-medium text-brand-teal-600 hover:text-brand-teal-700 hover:underline">
                        View all notifications
                    </Link>
                </div>
            )}
        </DashCard>
    );
}

const CELEBRATION_ICONS = {
    birthday: Cake,
    work: Briefcase,
    marriage: Heart,
};

/* ─── Celebrations panel ─── */
export function CelebrationsCard({ events, showTeamTab = true }) {
    const [scope, setScope] = useState(showTeamTab ? 'team' : 'clients');
    const [filter, setFilter] = useState('all');

    const scoped = events.filter(e => e.scope === scope);
    const filtered = filter === 'all' ? scoped : scoped.filter(e => e.type === filter);
    const today = filtered.filter(e => e.isToday);
    const upcoming = filtered.filter(e => !e.isToday);

    const tabs = showTeamTab
        ? [{ id: 'team', label: 'Team' }, { id: 'clients', label: 'Clients' }]
        : [{ id: 'clients', label: 'Clients' }];

    const filters = [
        { id: 'all', label: 'All' },
        { id: 'birthday', label: 'Birthday' },
        { id: 'work', label: 'Work' },
        { id: 'marriage', label: 'Marriage' },
    ];

    const renderItem = (item) => {
        const Icon = CELEBRATION_ICONS[item.type] || Gift;
        const inner = (
            <div className="flex items-center gap-3 py-2">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-teal-50/80 text-brand-teal-600 ring-1 ring-brand-teal/10">
                    <Icon className="h-3.5 w-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-ink-800 truncate">{item.name}</p>
                    <p className="text-[10px] text-ink-400">{item.subtitle}</p>
                </div>
                <span className="text-[11px] font-medium text-brand-teal-700 tabular-nums shrink-0">{item.dateLabel}</span>
            </div>
        );
        return item.href ? (
            <Link key={item.id} to={item.href} className="block rounded-lg hover:bg-brand-teal-50/50 px-1 -mx-1 transition">
                {inner}
            </Link>
        ) : (
            <div key={item.id} className="px-1 -mx-1">{inner}</div>
        );
    };

    return (
        <DashCard className="h-full flex flex-col overflow-hidden p-0">
            <div className="px-4 pt-4 pb-3 border-b border-ink-100/80 bg-gradient-to-br from-brand-teal-50/20 to-white/90 shrink-0">
                <div className="flex items-center gap-2 mb-3">
                    <span className="grid h-8 w-8 place-items-center rounded-xl bg-brand-teal-50 text-brand-teal-600 ring-1 ring-brand-teal/10">
                        <Gift className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <h3 className="text-sm font-medium text-ink-800">Celebrations</h3>
                </div>

                <div className="flex rounded-lg bg-ink-50/80 p-0.5 gap-0.5">
                    {tabs.map(t => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setScope(t.id)}
                            className={`flex-1 rounded-md py-1.5 text-[11px] font-medium transition ${
                                scope === t.id ? 'bg-white text-brand-teal-700 shadow-sm ring-1 ring-ink-100/60' : 'text-ink-400 hover:text-ink-600'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {filters.map(f => (
                        <button
                            key={f.id}
                            type="button"
                            onClick={() => setFilter(f.id)}
                            className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition ${
                                filter === f.id
                                    ? 'bg-brand-teal-50 text-brand-teal-700 ring-1 ring-brand-teal/20'
                                    : 'bg-white/80 text-ink-500 ring-1 ring-ink-100/80 hover:ring-brand-teal/20 hover:text-ink-600'
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0 space-y-4">
                {filtered.length === 0 ? (
                    <div className="text-center py-8">
                        <Sparkles className="h-8 w-8 text-ink-300 mx-auto mb-2" />
                        <p className="text-xs text-ink-500">No celebrations in the next 45 days</p>
                    </div>
                ) : (
                    <>
                        {today.length > 0 && (
                            <div>
                                <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-brand-teal-600 mb-1">
                                    <PartyPopper className="h-3.5 w-3.5" /> Today
                                </p>
                                <div className="divide-y divide-ink-100">{today.map(renderItem)}</div>
                            </div>
                        )}
                        {upcoming.length > 0 && (
                            <div>
                                <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-ink-400 mb-1">Upcoming</p>
                                <div className="divide-y divide-ink-100">{upcoming.map(renderItem)}</div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </DashCard>
    );
}

/* ─── Collection rate — half-width row 2 card ─── */
export function CollectionRateCard({ rate, portfolio, pendingItems, pendingLink }) {
    const pendingTotal = pendingItems.reduce((s, i) => s + (i.count || 0), 0);

    return (
        <DashCard className="h-full flex flex-col overflow-visible">
            <div className="flex items-start justify-between gap-2 mb-2 shrink-0">
                <div>
                    <h3 className="text-sm font-medium text-ink-800">Collection rate</h3>
                    <p className="text-xs text-ink-400 mt-0.5">Collected vs total payable</p>
                </div>
                {pendingLink && pendingTotal > 0 && (
                    <Link to={pendingLink} className="text-[11px] font-medium text-brand-teal-600 hover:text-brand-teal-700 hover:underline shrink-0">
                        {pendingTotal} pending →
                    </Link>
                )}
            </div>

            <div className="flex-1 flex flex-col sm:flex-row items-center gap-4 min-h-0">
                <div className="w-full sm:w-[48%] shrink-0">
                    <SemiGaugeCard value={rate} max={100} displayValue={`${rate}%`} compact />
                </div>
                <div className="flex-1 w-full grid grid-cols-2 gap-2">
                    {[
                        { label: 'Collected', value: inr(portfolio.totalCollected), tone: 'text-brand-teal-700' },
                        { label: 'Payable', value: inr(portfolio.totalPayable), tone: 'text-ink-800' },
                        { label: 'Balance due', value: inr(portfolio.totalBalance), tone: portfolio.totalBalance > 0 ? 'text-rose-600' : 'text-ink-800' },
                        { label: 'Deal value', value: inr(portfolio.totalDeal), tone: 'text-ink-800' },
                    ].map(s => (
                        <div key={s.label} className={`rounded-lg bg-white px-3 py-2.5 ${SOFT_CARD_EDGE}`}>
                            <p className="text-[9px] uppercase tracking-[0.14em] text-ink-500">{s.label}</p>
                            <p className={`text-sm font-medium tabular-nums mt-0.5 truncate ${s.tone}`}>{s.value}</p>
                        </div>
                    ))}
                </div>
            </div>
        </DashCard>
    );
}

const PERFORMER_DESIGNATIONS = [
    { id: 'all', label: 'All' },
    { id: 'bde', label: 'BDE' },
    { id: 'bdm', label: 'BDM' },
    { id: 'bda', label: 'BDA' },
];

function rankTone(rank) {
    if (rank === 1) return 'bg-amber-50 text-amber-700';
    if (rank === 2) return 'bg-slate-100 text-slate-700';
    if (rank === 3) return 'bg-orange-50 text-orange-700';
    return 'bg-ink-100 text-ink-600';
}

export function TopPerformersSection({ performers = [], monthlyPerformers = [] }) {
    const [period, setPeriod] = useState('all');
    const [designation, setDesignation] = useState('all');

    const source = period === 'monthly' ? monthlyPerformers : performers;
    const filtered = designation === 'all'
        ? source
        : source.filter(p => p.designation === designation);
    const rows = filtered.slice(0, 5);
    return (
        <DashCard className="overflow-hidden p-0">
            <div className="px-4 py-3 border-b border-ink-100/80 bg-gradient-to-r from-brand-teal-50/15 via-white to-white">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="text-base font-medium text-ink-800">Top 5 Performers</p>
                        <p className="text-xs text-ink-500">Ranked by total received amount</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="inline-flex rounded-full p-1 bg-ink-50 ring-1 ring-ink-100/80">
                            <button
                                type="button"
                                onClick={() => setPeriod('all')}
                                className={`px-3 py-1 text-[11px] rounded-full font-medium transition ${
                                    period === 'all'
                                        ? 'bg-white text-brand-teal-700 ring-1 ring-ink-200/80'
                                        : 'text-ink-500'
                                }`}
                            >
                                All Time
                            </button>
                            <button
                                type="button"
                                onClick={() => setPeriod('monthly')}
                                className={`px-3 py-1 text-[11px] rounded-full font-medium transition ${
                                    period === 'monthly'
                                        ? 'bg-white text-brand-teal-700 ring-1 ring-ink-200/80'
                                        : 'text-ink-500'
                                }`}
                            >
                                Monthly
                            </button>
                        </div>
                        <div className="inline-flex rounded-full p-1 bg-ink-50 ring-1 ring-ink-100/80">
                            {PERFORMER_DESIGNATIONS.map(opt => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => setDesignation(opt.id)}
                                    className={`px-2.5 py-1 text-[11px] rounded-full font-medium transition ${
                                        designation === opt.id
                                            ? 'bg-[#1367af] text-white'
                                            : 'text-ink-500'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-3 space-y-2">
                {rows.length === 0 ? (
                    <div className="rounded-xl bg-white px-4 py-8 text-center text-sm text-ink-400">No performer data available</div>
                ) : rows.map((p, idx) => {
                    const rank = idx + 1;
                    const initial = (p.name || '?').trim().charAt(0).toUpperCase();
                    return (
                        <div key={p.id} className="rounded-xl bg-white px-3 py-3 ring-1 ring-ink-100/70 hover:ring-brand-teal/25 hover:bg-brand-teal-50/[0.08] transition">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2.5 min-w-0">
                                    <span className={`grid h-8 w-8 place-items-center rounded-full text-xs font-medium ${rankTone(rank)}`}>
                                        {rank}
                                    </span>
                                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-teal-50 text-brand-teal-700 text-sm font-medium">
                                        {initial}
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-ink-800 truncate">{p.name}</p>
                                        <p className="text-[11px] text-ink-400">{p.clients} client{p.clients === 1 ? '' : 's'}</p>
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-base font-medium text-[#1367af] tabular-nums">{inr(p.collected)}</p>
                                    <span className="inline-flex mt-0.5 rounded-full bg-ink-50 px-2 py-0.5 text-[10px] font-medium text-ink-500 uppercase ring-1 ring-ink-100/80">
                                        {(p.designation || 'other').toUpperCase()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </DashCard>
    );
}

export function StatusBadge({ status, colors = CLIENT_STATUS_COLOR }) {
    const color = colors[status] || '#9ca3af';
    return (
        <span className="inline-block text-[11px] font-semibold capitalize px-2 py-0.5 rounded-md" style={{ backgroundColor: `${color}18`, color }}>
            {status}
        </span>
    );
}

export function ClientTable({ title, linkTo, rows, emptyText, showBy = true, compact = false }) {
    return (
        <DashCard className="overflow-hidden p-0">
            <div className="flex items-center justify-between px-3 py-2 border-b border-ink-100">
                <h2 className="text-sm font-bold text-ink-900">{title}</h2>
                {linkTo && <Link to={linkTo} className="text-[10px] font-semibold text-brand-teal-700 hover:underline">View all →</Link>}
            </div>
            {rows.length === 0 ? (
                <p className="text-xs text-ink-500 py-6 text-center">{emptyText}</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className={`ag-table ${compact ? 'text-xs' : ''}`}>
                        <thead>
                            <tr>
                                <th>Client</th>
                                {showBy && <th>Submitted by</th>}
                                <th className="text-right">Deal</th>
                                <th className="text-right">Collected</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} className="hover:bg-brand-teal-50/20">
                                    <td>
                                        <Link to={r.href} className="font-medium hover:text-brand-teal-700">{r.name}</Link>
                                        <p className="text-[11px] text-ink-500">{r.date}</p>
                                    </td>
                                    {showBy && <td className="text-sm text-ink-600">{r.by}</td>}
                                    <td className="text-right font-semibold tabular-nums">{r.deal}</td>
                                    <td className="text-right tabular-nums text-sm">{r.pct}</td>
                                    <td><StatusBadge status={r.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </DashCard>
    );
}
